import styles from "./index.module.scss";
import { useTranslation } from "react-i18next";
import {
    ChangePasswordBindingModel,
    ProfileBindingModel
} from "@switcherstudio/switcher-api-client";
import rollbar from "helpers/rollbar";
import { useCallback, useMemo, useState } from "react";
import { useSwitcherClient } from "hooks/useSwitcherClient";
import { useDispatch } from "react-redux";
import { AppDispatch } from "store/store";
import { addNotification } from "store/notification/slice";
import { NotificationType } from "store/notification/types";
import { refreshToken, getUserInfo } from "store/user/thunks";
import { EditProfileForm } from "./EditProfileForm";
import { ChangePasswordForm } from "./ChangePasswordForm";
import { LoadingOverlay } from "components/loading-overlay";
import { AccountDeleteModal } from "components/modal/AccountDeleteModal";
import { usePauseStatus } from "hooks/usePauseStatus";
import { ThemeSelection } from "components/theme-selection";

export const ProfilePage: React.FC = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch<AppDispatch>();
    const [hasActiveSubscription, setHasActiveSubscription] =
        useState<boolean>(false);
    const [isAccountDeleteModalOpen, setIsAccountDeleteModalOpen] =
        useState(false);
    const { userIsPaused } = usePauseStatus();
    const [isReloading, setIsReloading] = useState(false);
    const [passwordFormKey, setPasswordFormKey] = useState(0);

    const {
        dispatchApiRequest: getProfile,
        data: profileData,
        loading: profileDataLoading
    } = useSwitcherClient((client) => client.account_GetProfile, {
        requestImmediately: true,
        onError(e) {
            rollbar.error("Error getting account profile", e);

            dispatch(
                addNotification({
                    type: NotificationType.Danger,
                    message: e.message
                })
            );
        },
        onSuccess: () => {
            setIsReloading(false);
        }
    });

    const { dispatchApiRequest: updateProfile, loading: updateProfileLoading } =
        useSwitcherClient((client) => client.account_PutProfile, {
            suppressErrorBoundary: true,
            requestImmediately: false,
            onSuccess: () => {
                dispatch(
                    addNotification({
                        type: NotificationType.Success,
                        message: "profile:success-message-profile"
                    })
                );
            },
            onError(e, response) {
                rollbar.error("Error updating user profile", e, response);

                dispatch(
                    addNotification({
                        type: NotificationType.Danger,
                        message: e.message
                    })
                );
            }
        });

    const {
        dispatchApiRequest: changePassword,
        loading: changePasswordLoading
    } = useSwitcherClient((client) => client.account_ChangePassword, {
        suppressErrorBoundary: true,
        requestImmediately: false,
        onSuccess: () => {
            setPasswordFormKey((k) => k + 1);
            dispatch(
                addNotification({
                    type: NotificationType.Success,
                    message: "messages:password-updated"
                })
            );
        },
        onError(e, response) {
            rollbar.error("Error changing password", e, response);

            dispatch(
                addNotification({
                    type: NotificationType.Danger,
                    message: e.message
                })
            );
        }
    });

    const onChangePasswordSubmit = useCallback(
        async (form: ChangePasswordBindingModel) => {
            await changePassword([
                {
                    OldPassword: form.OldPassword,
                    NewPassword: form.NewPassword,
                    ConfirmPassword: form.ConfirmPassword
                }
            ]);
        },
        [changePassword]
    );

    const onEditProfileSubmit = useCallback(
        async (form: ProfileBindingModel) => {
            setIsReloading(true);
            await updateProfile([form]);
            await dispatch(refreshToken());
            await dispatch(getUserInfo());
            await getProfile();
        },
        [dispatch, updateProfile, getProfile]
    );

    const isLoading = useMemo(() => {
        return (
            profileDataLoading ||
            updateProfileLoading ||
            changePasswordLoading ||
            isReloading
        );
    }, [
        profileDataLoading,
        updateProfileLoading,
        changePasswordLoading,
        isReloading
    ]);

    const { data: customer } = useSwitcherClient(
        (client) => client.stripe_GetCustomer,
        {
            requestImmediately: true,
            onError: (e) => {
                rollbar.error(
                    "Error getting user stripe customer subscription info for account delete modal",
                    e
                );
            },
            onSuccess: (data) => {
                const subscription = data?.StripeSubscriptions?.[0];
                if (subscription) {
                    if (
                        (subscription.Status === "active" ||
                            subscription.Status === "trialing" ||
                            userIsPaused) &&
                        !subscription.CancelAtPeriodEnd
                    ) {
                        setHasActiveSubscription(true);
                    } else {
                        setHasActiveSubscription(false);
                    }
                }
            }
        }
    );

    /** If user has active subscription, throw error toast. Else, allow them into deletion modal. */
    const onDeleteAccountClick = useCallback(async () => {
        if (customer) {
            if (hasActiveSubscription) {
                dispatch(
                    addNotification({
                        type: NotificationType.Danger,
                        message: "errors:user-has-active-subscription"
                    })
                );
            } else {
                setIsAccountDeleteModalOpen(true);
            }
        } else {
            dispatch(
                addNotification({
                    type: NotificationType.Danger,
                    message: "errors:load-fail"
                })
            );
        }
    }, [customer, dispatch, hasActiveSubscription]);

    return (
        <>
            <div className={styles["profile-form-container"]}>
                <LoadingOverlay isLoading={isLoading}>
                    {profileData && (
                        <>
                            <EditProfileForm
                                onSubmit={onEditProfileSubmit}
                                profile={profileData}
                                loading={
                                    profileDataLoading || updateProfileLoading
                                }
                            />

                            <hr />

                            <ChangePasswordForm
                                key={passwordFormKey}
                                onSubmit={onChangePasswordSubmit}
                                loading={changePasswordLoading}
                            />
                            <hr />

                            <ThemeSelection />
                            <hr />

                            <div className="row">
                                <div className="col-8">
                                    <button
                                        className={
                                            styles["delete-account-link"]
                                        }
                                        onClick={() => onDeleteAccountClick()}
                                    >
                                        {t(
                                            "delete-account:delete-account"
                                        ).toUpperCase()}
                                    </button>
                                    <p
                                        className={
                                            styles["delete-account-description"]
                                        }
                                    >
                                        {t(
                                            "profile:delete-account-description"
                                        )}
                                    </p>
                                </div>
                            </div>
                        </>
                    )}
                </LoadingOverlay>
            </div>
            <AccountDeleteModal
                isOpen={isAccountDeleteModalOpen}
                setIsOpen={() => setIsAccountDeleteModalOpen(false)}
            />
        </>
    );
};
